import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Radio, X, Waves, Check } from "lucide-react";
import { APPS, SITES, launchTarget, openQuery } from "@/data/launcher";

const ROUTES = [
  { keys: ["training", "workout", "gym", "calisthenics"], to: "/training", label: "Training" },
  { keys: ["coach", "coaching", "screen"], to: "/coach", label: "Live Coach" },
  { keys: ["library", "knowledge"], to: "/library", label: "Library" },
  { keys: ["nutrition", "diet", "food", "meal"], to: "/nutrition", label: "Nutrition" },
  { keys: ["channel", "chat", "talk"], to: "/channel", label: "Channel" },
  { keys: ["system", "character", "stats", "profile", "awaken"], to: "/system", label: "System" },
  { keys: ["hub", "home", "menu", "cortex"], to: "/hub", label: "Hub" },
];

export default function VoiceAssistant() {
  const navigate = useNavigate();
  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!SR;

  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState("");
  const [heard, setHeard] = useState("");
  const [enroll, setEnroll] = useState(false);
  const [enrollCount, setEnrollCount] = useState(0);

  const recRef = useRef(null);
  const runningRef = useRef(false);
  const armedRef = useRef(false);
  const enabledRef = useRef(false);
  const enrollRef = useRef(false);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { enrollRef.current = enroll; }, [enroll]);

  const flash = (t) => { setStatus(t); };

  const runCommand = useCallback((raw) => {
    const cmd = (raw || "").toLowerCase().trim();
    if (!cmd) { armedRef.current = true; flash("Yes? Awaiting your command…"); return; }
    setHeard(cmd);
    // launch apps / sites (built-in + custom)
    let customs = [];
    try { customs = JSON.parse(localStorage.getItem("archon_custom_nodes") || "[]"); } catch { /* */ }
    const customTargets = customs.map((c) => ({ name: c.name, id: (c.name || "").toLowerCase(), _custom: c }));
    for (const t of [...APPS, ...SITES, ...customTargets]) {
      if (cmd.includes((t.name || "").toLowerCase()) || (t.id && cmd.includes(t.id))) {
        if (t._custom) launchTarget(t._custom.kind === "uri" ? { uri: t._custom.target } : { web: t._custom.target });
        else launchTarget(t);
        flash(`Launching ${t.name}…`); return;
      }
    }
    // search intents
    const m = cmd.match(/(?:search|google|look up|find)\s+(?:for\s+)?(.*)/);
    if (m && m[1]) { openQuery(m[1]); flash(`Searching: ${m[1]}`); return; }
    // navigation
    for (const r of ROUTES) {
      if (r.keys.some((k) => cmd.includes(k))) { navigate(r.to); flash(`Opening ${r.label}…`); return; }
    }
    // not recognized — avoid noisy auto-search
    flash(`Not recognized: “${cmd}”. Try “open training” or “search …”.`);
  }, [navigate]);

  const handleTranscript = useCallback((sLower) => {
    if (enrollRef.current) {
      if (sLower.includes("archon")) {
        setEnrollCount((c) => {
          const n = c + 1;
          if (n >= 3) {
            localStorage.setItem("archon_enrolled", "1");
            setEnroll(false);
            setEnabled(true);
            flash("Calibration complete. Listening for “ARCHON”.");
          }
          return n;
        });
      }
      return;
    }
    if (sLower.includes("archon")) {
      const after = sLower.split("archon").pop().trim();
      runCommand(after);
    } else if (armedRef.current) {
      armedRef.current = false;
      runCommand(sLower);
    }
  }, [runCommand]);

  const startRec = useCallback(() => {
    if (!supported || runningRef.current) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (ev) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) handleTranscript(res[0].transcript.toLowerCase());
      }
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed") { setEnabled(false); flash("Microphone blocked."); }
      else if (e.error === "network") { flash("Voice network error — retrying…"); }
    };
    rec.onend = () => {
      runningRef.current = false;
      if (enabledRef.current || enrollRef.current) { try { rec.start(); runningRef.current = true; } catch { /* */ } }
    };
    try { rec.start(); runningRef.current = true; recRef.current = rec; } catch { /* */ }
  }, [SR, supported, handleTranscript]);

  const stopRec = useCallback(() => {
    runningRef.current = false;
    try { recRef.current && recRef.current.stop(); } catch { /* */ }
  }, []);

  useEffect(() => {
    if (enabled || enroll) startRec(); else stopRec();
    return () => stopRec();
  }, [enabled, enroll, startRec, stopRec]);

  const toggle = () => {
    if (!supported) { flash("Voice needs Chrome or Edge on desktop."); return; }
    if (enabled) { setEnabled(false); flash(""); return; }
    if (localStorage.getItem("archon_enrolled")) { setEnabled(true); flash("Listening for “ARCHON”."); }
    else { setEnrollCount(0); setEnroll(true); }
  };

  return (
    <>
      {/* Status chip */}
      {(enabled || status) && !enroll && (
        <div className="fixed bottom-24 right-5 z-[60] max-w-[240px]" data-testid="voice-status">
          <div className="flex items-center gap-2 bg-[#0A0E17]/90 border border-cyan-400/30 rounded-full px-3 py-2 backdrop-blur text-xs text-slate-200 shadow-lg">
            {enabled && <Radio size={13} className="text-cyan-400 animate-pulse shrink-0" />}
            <span className="truncate">{status || "Listening for “ARCHON”…"}</span>
          </div>
          {heard && enabled && <p className="mono-label text-right mt-1 pr-2 opacity-70">“{heard}”</p>}
        </div>
      )}

      {/* Toggle button */}
      <button data-testid="voice-assistant-toggle" onClick={toggle} title="ARCHON voice"
        className={`fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full grid place-items-center border backdrop-blur-xl transition-all shadow-lg ${enabled ? "bg-cyan-400 text-black border-cyan-300 shadow-[0_0_26px_rgba(0,240,255,0.6)]" : "bg-[#0A0E17]/90 text-slate-200 border-white/15 hover:border-white/40"}`}>
        {enabled ? <Mic size={22} /> : supported ? <MicOff size={22} /> : <MicOff size={22} className="opacity-50" />}
      </button>

      {/* Enrollment modal */}
      {enroll && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm px-6" data-testid="enroll-modal">
          <div className="glass rounded-2xl p-8 max-w-md w-full text-center border border-cyan-400/30">
            <button onClick={() => { setEnroll(false); flash(""); }} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="enroll-close"><X size={18} /></button>
            <Waves className="text-cyan-400 mx-auto animate-pulse" size={40} />
            <p className="mono-label mt-5">VOICE CALIBRATION</p>
            <h2 className="font-display text-2xl font-bold mt-2">Attune ARCHON to your voice</h2>
            <p className="text-slate-400 text-sm mt-3">
              Say <span className="text-cyan-300 font-semibold">“ARCHON”</span> clearly, three times. This samples your wake word so the System recognizes when you summon it.
            </p>
            <div className="flex justify-center gap-3 mt-6">
              {[0, 1, 2].map((i) => (
                <span key={i} data-testid={`enroll-dot-${i}`} className={`w-12 h-12 rounded-full grid place-items-center border-2 transition-all ${i < enrollCount ? "bg-cyan-400 border-cyan-300 text-black" : "border-blue-800 text-slate-600"}`}>
                  {i < enrollCount ? <Check size={18} /> : i + 1}
                </span>
              ))}
            </div>
            <p className="mono-label mt-6 flex items-center justify-center gap-2"><Radio size={12} className="text-cyan-400 animate-pulse" /> LISTENING… say “ARCHON”</p>
            <p className="text-[0.7rem] text-slate-600 mt-4">Note: the browser can't lock to only your voice — calibration tunes recognition and enables the wake word.</p>
          </div>
        </div>
      )}
    </>
  );
}
